import React from 'react';
import {useQueryClient} from 'react-query';
import {Button, Dialog, DialogTitle, Divider, Grid} from '@mui/material';
import {Formik} from 'formik';
import * as Yup from 'yup';
import IyoAuth from '../../../auth/Auth';
import IyoFormikTextField from '../../common/IyoFormikTextField';
import IyoFormikDropDown from '../../common/IyoFormikDropDown';

const ACTIVE_OPTIONS = [
  {name: 'true', value: 1},
  {name: 'false', value: 0}
];

/**
 * Create/edit dialog shared by the Category and Sub-Category tabs. Both tables carry the
 * same shape (name, description, active) and differ only in whether a parent category has
 * to be picked, so `parentOptions` is what separates them: pass it for Sub-Category and
 * the category_id dropdown appears and becomes required.
 */
const CategoryEditDialog = ({entityLabel, service, queryKey, parentOptions, state, setState}) => {
  const queryClient = useQueryClient();
  const isEdit = state.editMode === 'edit';
  const row = state.rowEditing;

  const initialValues = {
    name: isEdit ? (row?.name ?? '') : '',
    description: isEdit ? (row?.description ?? '') : '',
    active: isEdit ? (row?.active ?? 1) : 1,
    ...(parentOptions ? {category_id: isEdit ? (row?.category_id ?? '') : ''} : {})
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required').max(255, 'Name is at most 255 characters'),
    description: Yup.string().nullable().max(255, 'Description is at most 255 characters'),
    // The initial value is '', which yup casts to NaN - typeError runs before required,
    // so without this the user sees yup's raw cast message instead of ours.
    ...(parentOptions ? {category_id: Yup.number()
      .typeError('Category is required')
      .required('Category is required')} : {})
  });

  const handleClose = () => setState.setEditOpen(false);

  const handleSubmit = async (values) => {
    try {
      const body = {...values, updated_by: await IyoAuth.getUser()};
      if (isEdit) {
        // The API's PUT validator wants the id in the body as well as the path.
        await service.put(row.id, {...body, id: row.id});
        setState.setMsgText(`${entityLabel} ${values.name} updated`);
      } else {
        const resp = await service.post(body);
        setState.setMsgText(`${entityLabel} created, id = ${resp?.id}`);
      }
      setState.setMsgOpen(true);
      setState.setEditOpen(false);
      await queryClient.invalidateQueries(queryKey);
    } catch (error) {
      setState.setErrDisplayText(`${error.message}` +
        `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`);
      setState.setErrorOpen(true);
    }
  };

  return (
    <Dialog open={state.editOpen} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? `Edit ${entityLabel}` : `Create ${entityLabel}`}</DialogTitle>
      <Divider/>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting}) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{p: '1.5rem'}}>
              <Grid item xs={12}>
                <IyoFormikTextField
                  name='name' label='Name' required
                  values={values} touched={touched} errors={errors}
                  handleBlur={handleBlur} handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <IyoFormikTextField
                  name='description' label='Description'
                  values={values} touched={touched} errors={errors}
                  handleBlur={handleBlur} handleChange={handleChange}
                />
              </Grid>
              {parentOptions && (
                <Grid item xs={12}>
                  <IyoFormikDropDown
                    name='category_id' label='Category' required dataList={parentOptions}
                    values={values} touched={touched} errors={errors}
                    handleBlur={handleBlur} handleChange={handleChange}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <IyoFormikDropDown
                  name='active' label='Active' dataList={ACTIVE_OPTIONS}
                  values={values} touched={touched} errors={errors}
                  handleBlur={handleBlur} handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end', gap: '0.5rem'}}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button type='submit' variant='contained' disabled={isSubmitting}>Save</Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CategoryEditDialog;
